<template>
  <button
    @click="handleClick"
    :class="[{
      small: size === 'small',
      regular: size === 'regular',
      large: size === 'large',
      contained: variant === 'contained',
      outlined: variant === 'outlined',
      text: variant === 'text',
      icon,
    }, contentClass]"
    :type="type"
  >
    <div class="outline" />
    <router-link v-if="url && url.indexOf('http') == -1" :title="label" :to="url">{{ label }}</router-link>
    <a v-else-if="url" :href="url" target="_blank" :title="label" rel="noopener">{{ label }}</a>
    <span v-else-if="label">{{ label }}</span>
    <slot v-else />
    <div class="zb-spin" />
  </button>
</template>
<script>
export default {
  props: {
    label: String,
    url: String,
    size: {
      type: String,
      default: "small"
    },
    variant: {
      type: String,
      default: "contained"
    },
    icon: {
      type: Boolean,
      default: false
    },
    type: {
      type: String,
      default: 'button'
    },
    contentClass: String,
  },
  methods: {
    handleClick(e) {
      this.$emit("click", e);
    }
  }
};
</script>
<style lang="scss">
$primary-color: #eb7d00;
$width: 20px;
$height: 20px;

.zb-spin {
  @keyframes rotate {
    from { transform: rotate(0deg);   }
    to   { transform: rotate(360deg); }
  }

  display: none;
  margin-left: 10px;
  animation: rotate  1s linear infinite;
  background: $primary-color;
  border-radius: 50%;
  height: $height;
  width: $width;
  position: relative;
  &:before,
  &:after {
    content: '';
    position: absolute;
  }

  &:before {
    border-radius: 50%;
    background:
      linear-gradient(0deg,   hsla(0, 0%, 100%, 0) 50%, hsla(0, 0%, 100%, 0.3) 100%)   0%   0%,
      linear-gradient(90deg,  hsla(0, 0%, 100%, 0.3)  0%, hsla(0, 0%, 100%, 0.6) 100%) 100%   0%,
      linear-gradient(180deg, hsla(0, 0%, 100%, 0.6)  0%, hsla(0, 0%, 100%, 0.9) 100%) 100% 100%,
      linear-gradient(360deg, hsla(0, 0%, 100%, 0.9)  0%, hsla(0, 0%, 100%, 1) 100%)   0% 100%
      ;
    background-repeat: no-repeat;
    background-size: 50% 50%;
    top: -1px;
    bottom: -1px;
    left: -1px;
    right: -1px;
  }

  &:after {
    background: $primary-color;
    border-radius: 50%;
    top: 3%;
    bottom: 3%;
    left: 3%;
    right: 3%;
  }
}

button[tertiary] {
  &.outlined {
    svg {
      path{
        fill: #1695a3;
      }
    }
  }
}
button[disabled] {
  background: transparent;
  color: #c2c2c2;
  border-color: #e0e0e0;
  svg {
    path{
      fill: #e0e0e0 !important;
    }
  }
}
</style>
<style lang="scss" scoped>
@import "../../assets/css/_color.scss";

button {
  font-size: inherit;
  border: 1px solid $border-color;
  display: inline-block;
  vertical-align: middle;
  text-decoration: none;
  background-color: white;
  text-align: center;
  cursor: pointer;
  white-space: nowrap;
  transition-duration: 0.4s;
  // padding: 13px 20px;
  color: #222;
  border-radius: 4px;
  border: none;
  font-weight: 500;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  .outline {
    border: 1px solid #222;
    position: absolute;
    width: calc(100% + 8px);
    height: calc(100% + 8px);
    border-radius: 6px;
    left: -4px;
    top: -4px;
    display: none;
  }
  svg {
    overflow: visible;
    path {
      fill: #222;
    }
    &:last-child {
      margin: 0;
    }
  }
  &:focus {
    outline: none;
  }
  &:active {
    .outline {
      display: block;
    }
  }
  & a {
    display: block;
    color: $secondary;
    padding: 0.6em 1em;
    margin: -0.6em -1em; //reset parent padding
    text-decoration: none;
    cursor: pointer;
    transition-duration: 0.4s;
  }
  &:hover,
  & > a:hover {
    background: #f5f5f5;
  }
  &:disabled {
    cursor: not-allowed;
    color: #c2c2c2;
    border-color: #e0e0e0;
    background-color: transparent;
    &:hover,
    & > a:hover {
      color: $disabled-text;
      border-color: $disabled;
      background-color: $disabled;
    }
    svg {
      path {
        fill: #c2c2c2;
      }
    }
  }
  &.small {
    font-size: 14px;
    line-height: 14px;
  }
  &.regular {
    font-size: 16px;
    line-height: 100%;
    padding: 16px 20px;
    &.contained {
      border-radius: 0.25em;
    }
  }
  &.large {
    font-size: 20px;
    line-height: 100%;
    padding: 14px 20px;
    &.contained {
      border-radius: 6px;
    }
  }
  &.text {
    background: transparent !important;
    border: none !important;
    &:hover {
      background: white;
    }
  }
  &.outlined {
    background: transparent;
    border: 1px solid;
    &:active {
      .outline {
        display: none;
      }
    }
    &:hover {
      background: white;
    }
  }
}

button[primary]:not([disabled]) {
  color: white;
  background-color: #eb7d00;
  svg {
    path {
      fill: white;
    }
  }
  &.loading {
    .zb-spin {
      display: block;
    }
  }
  & a {
    color: $secondary-text;
  }
  &:hover,
  & > a:hover {
    background-color: #f38a0e;
  }
  &.outlined {
    background: transparent;
    border: 1px solid;
    &:hover {
      background: white;
    }
    border-color: #eb7d00;
    color: #eb7d00;
    svg {
      path {
        fill: #eb7d00;
      }
    }
  }
  &.text {
    color: #eb7d00;
    svg {
      path {
        fill: #eb7d00;
      }
    }
  }
}

button[secondary]:not([disabled]) {
  color: white;
  background-color: #0f2644;
  svg {
    path {
      fill: white;
    }
  }
  &:hover {
    background-color: #245088;
  }
  &.outlined {
    background: transparent;
    border: 1px solid;
    &:hover {
      background: white;
    }
    border-color: #0f2644;
    color: #0f2644;
    svg {
      path {
        fill: #0f2644;
      }
    }
  }
  &.text {
    color: #0f2644;
    svg {
      path {
        fill: #0f2644;
      }
    }
  }
}

button[tertiary]:not([disabled]) {
  color: white;
  background-color: #1695a3;
  svg {
    path {
      fill: white;
    }
  }
  &:hover {
    background-color: #25b6c6;
  }
  &.outlined {
    background: transparent;
    border: 1px solid;
    &:hover {
      background: white;
    }
    border-color: #1695a3;
    color: #1695a3;
    svg {
      path{
        fill: #1695a3;
      }
    }
  }
  &.text {
    color: #1695a3;
    svg {
      path {
        fill: #1695a3;
      }
    }
  }
}

button[disabled] {
  background: transparent;
  color: #c2c2c2;
  border-color: #e0e0e0;
  svg {
    path{
      fill: #e0e0e0;
    }
  }
}
</style>
